import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import addNotification from "react-push-notification";
import { getAllProjects, getTwilioChatToken } from "../services/chatServices";
import {
    getChatUser,
    getCompanyId,
    getWebRole,
    getInstrId,
} from "../util/localStorage";
import { sendMsg } from "../assets/images";
import { GetAccountNotifications } from "../services/notifications";

const ChatContext = createContext();

export { ChatContext };

export const useChatContext = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error("useChatContext must be used within a ChatProvider");
    }
    return context;
};

export const ChatProvider = ({ children }) => {
    const [globalUnreadCount, setGlobalUnreadCount] = useState(0);
    const [twilioClient, setTwilioClient] = useState(null);
    const [projects, setProjects] = useState([]);
    const [titleChange, setTitleChange] = useState(false);
    const [activeConversationId, setActiveConversationId] = useState(null);
    const [allowMessageNotifications, setAllowMessageNotifications] = useState(true);
    const [allowMentionNotifications, setAllowMentionNotifications] = useState(true);
    const [allowMentionEmails, setAllowMentionEmails] = useState(true);

    const activeConversationIdRef = useRef(null);
    const recalcTimerRef = useRef(null);

    const userChatEmail = getChatUser();
    const data = getCompanyId();
    const role = getWebRole();
    const instrId = getInstrId();
    const path = window.location.pathname;
    const Conversations = require("@twilio/conversations");

    // Title change functionality
    const changeTitle = (msg) => {
        document.title = msg;
    };

    const handleVisibilityChange = () => {
        if (document.hidden) {
            // Tab is not visible (user switched to another tab or minimized the browser)
        } else {
            // Tab is visible (user switched back to the tab)
            setTitleChange(false);
        }
    };

    // Check if chat is active for notification logic
    const isChatActive = (message) => {
        // Sender is the author
        if (message?.author === userChatEmail) {
            return true;
        }

        const currentPath = window.location.pathname;

        // In the main chat with selected conversation
        if (currentPath === "/chats") {
            const messageConversationId =
                message?.conversation?._internalState?.uniqueName;
            return messageConversationId === activeConversationId;
        }

        // Check for project chat routes
        const projectChatRegex =
            /\/(jobcenter\/inprogress\/job-details\/(\d+)\/5|client-job\/job-entity\/7\/(\d+))/;
        const match = currentPath.match(projectChatRegex);
        if (!match) {
            return false;
        }

        // Extract project ID from URL (could be in position 2 or 3 depending on route)
        const urlProjectId = match[2] || match[3];

        // Get project ID from message attributes or conversation attributes
        const messageProjectId =
            message?.attributes?.projectId ||
            message?.conversation?._internalState?.attributes?.projectId;

        // Check if we're on the right project
        if (urlProjectId !== String(messageProjectId)) {
            return false;
        }

        const searchParams = new URLSearchParams(window.location.search);
        let email = searchParams.get("email");
        let groupId = searchParams.get("groupId");
        if (!groupId && email && email.trim() === message?.author?.trim()) return true;
        if (!email && groupId && groupId === message.attributes?.groupId) return true;

        return false;
    };

    const getIsDirectChat = (conversation) => {
        var result = false;
        if (!conversation) return result;
        
        if (conversation?._participants.size == 2) {
            let emails = []
            conversation?._participants.forEach((participant, _) => {
                emails.push(participant.identity);
            });
            if (emails.includes(userChatEmail)) {
                result = true;
            }
        }

        return result;
    };

    const containsMention = (message) => {
        const mentionPattern = /\+\+([^\-]+)-([^\+]+)\+\+/g;
        const sanitized = message.body.replace(mentionPattern, (_, _email, name) => `@${name}`);
        return sanitized !== message.body ? sanitized : null;
    };

    // Send notification for new messages
    const sendNewMessageNotification = (e) => {
        if (e && !isChatActive(e) && e.state.attributes?.main) {
            setTitleChange(true);
            const isDirectChat = getIsDirectChat(e.conversation);
            const mentionText = containsMention(e); // sanitized string with @name if mention exists, null otherwise

            var canSendNotification = (
                (mentionText && allowMentionNotifications) || (!mentionText && allowMessageNotifications));

            canSendNotification && addNotification({
                title: !isDirectChat
                    ? `${e.state.attributes?.name} - ${e.state.attributes?.groupName}`
                    : `${e.state.attributes?.name}`,
                message:
                    e.state.attributes?.type === "media"
                        ? `${e.state.attributes?.fileArray.length} file(s) sent`
                        : mentionText ?? e.state.body,
                duration: 5000,
                icon: sendMsg,
                onClick: () => {
                    if (data > 0 && role === "3") {
                        // Client
                        if (!isDirectChat) {
                            window.open(
                                `${window.location.origin}/client-job/job-entity/7/${e.conversation?._internalState?.attributes?.projectId}?groupId=${e.state.attributes?.groupId}`,
                                "_blank"
                            );
                        } else {
                            window.open(
                                `${window.location.origin}/client-job/job-entity/7/${e.conversation?._internalState?.attributes?.projectId}?email=${e.state?.author}`,
                                "_blank"
                            );
                        }
                    } else if (instrId > 0 && role === "2") {
                        // Talent
                        if (!isDirectChat) {
                            window.open(
                                `${window.location.origin}/jobcenter/inprogress/job-details/${e.conversation?._internalState?.attributes?.projectId}/5?groupId=${e.state.attributes?.groupId}`,
                                "_blank"
                            );
                        } else {
                            window.open(
                                `${window.location.origin}/jobcenter/inprogress/job-details/${e.conversation?._internalState?.attributes?.projectId}/5?email=${e.state?.author}`,
                                "_blank"
                            );
                        }
                    }
                },
                native: true, // when using native, your OS will handle theming.
            });
        }
    };

    useEffect(() => {
        // Setup visibility change listener
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
        };
    }, []);

    // Clear active conversation when not on chats page
    useEffect(() => {
        if (path !== "/chats") {
            setActiveConversationId(null);
        }
    }, [path]);

    useEffect(() => {
        // Handle title change animation
        let intervalId;
        if (titleChange) {
            intervalId = setInterval(() => {
                changeTitle(
                    document.title === "New Message"
                        ? "TTA Connect"
                        : "New Message"
                );
            }, 1000); // Update every 1000 milliseconds (1 second)
        } else {
            setTitleChange(false);
            clearInterval(intervalId);
            changeTitle("TTA Connect");
        }

        return () => clearInterval(intervalId);
    }, [titleChange]);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (userChatEmail) {
            initializeChatSystem();
        }

        // Cleanup function
        return () => {
            if (twilioClient) {
                twilioClient.removeAllListeners();
                twilioClient.shutdown();
            }
        };
    }, [userChatEmail]);
    /* eslint-enable react-hooks/exhaustive-deps */

    // Set up event listeners when both client and projects are available
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (twilioClient && projects.length > 0) {
            const handleMessageAdded = (e) => {
                sendNewMessageNotification(e);
                if (e?.state?.author !== userChatEmail && !isChatActive(e)) {
                    scheduleRecalculation(twilioClient, projects, 300);
                }
            };

            const handleMessageUpdated = (e) => {
                if (!isChatActive(e.message)) {
                    scheduleRecalculation(twilioClient, projects, 500);
                }
            };

            const handleConversationUpdated = (e) => {
                // Only recalculate for attribute changes, not for lastReadMessageIndex updates
                // (those are triggered by our own setAllMessagesRead calls)
                if (e.updateReasons?.includes("attributes")) {
                    scheduleRecalculation(twilioClient, projects, 1000);
                }
            };

            twilioClient.on("messageAdded", handleMessageAdded);
            twilioClient.on("messageUpdated", handleMessageUpdated);
            twilioClient.on("conversationUpdated", handleConversationUpdated);

            return () => {
                twilioClient.removeListener("messageAdded", handleMessageAdded);
                twilioClient.removeListener(
                    "messageUpdated",
                    handleMessageUpdated
                );
                twilioClient.removeListener(
                    "conversationUpdated",
                    handleConversationUpdated
                );
            };
        }
    }, [twilioClient, projects, activeConversationId]);
    /* eslint-enable react-hooks/exhaustive-deps */

    const getNotificationPreferences = async () => {
        GetAccountNotifications()
            .then((res) => {
                const userNotificationPreferences = res.data;

                userNotificationPreferences.forEach((pref) => {
                    if (pref.name === "TTA Messages") {
                        setAllowMessageNotifications(pref.hasAppNot);
                    }
                    else if (pref.name === "TTA Project Messages") {
                        setAllowMentionNotifications(pref.hasAppNot);
                        setAllowMentionEmails(pref.hasEmailNot);
                    }
                });
            })
            .catch((err) => {
                console.error(`Error getting notification preferences: ${err}`);
                setAllowMessageNotifications(false);
                setAllowMentionNotifications(false);
            });
    };

    const initializeChatSystem = async () => {
        try {
            // Get chat token
            const tokenResponse = await getTwilioChatToken({
                email: userChatEmail,
            });

            if (tokenResponse?.data) {
                await getNotificationPreferences();
                const client = new Conversations.Client(tokenResponse.data);
                setTwilioClient(client);

                // Get projects data
                const projectsResponse = await getAllProjects();
                if (projectsResponse?.data) {
                    setProjects(projectsResponse.data);

                    // Calculate initial unread counts (single call, no duplicate)
                    calculateGlobalUnreadCount(client, projectsResponse.data);
                }
            }
        } catch (error) {
            console.error("Error initializing chat system:", error);
        }
    };

    const scheduleRecalculation = useCallback((client, projectsData, delay = 300) => {
        if (recalcTimerRef.current) {
            clearTimeout(recalcTimerRef.current);
        }
        recalcTimerRef.current = setTimeout(() => {
            recalcTimerRef.current = null;
            calculateGlobalUnreadCount(client, projectsData);
        }, delay);
    }, []);

    const calculateGlobalUnreadCount = async (client, projectsData) => {
        if (!client) {
            console.error(
                "No Twilio client provided to calculateGlobalUnreadCount"
            );
            return;
        }

        if (!projectsData || !Array.isArray(projectsData)) {
            console.error(
                "Invalid projects data provided to calculateGlobalUnreadCount:",
                projectsData
            );
            return;
        }

        let totalUnreadCount = 0;

        try {
            // Collect all subscribed conversations across all pages
            let allConversations = [];
            let page = await client.getSubscribedConversations();
            allConversations.push(...page.items);
            while (page.hasNextPage) {
                page = await page.nextPage();
                allConversations.push(...page.items);
            }

            const conversationMap = new Map();

            // Use the ref to get the most current activeConversationId
            const currentActiveId = activeConversationIdRef.current;

            // Create a map of conversation unique names to their unread counts
            await Promise.all(
                allConversations.map(async (conv) => {
                    try {
                        // Skip the active conversation (user is currently viewing it)
                        if (conv.uniqueName === currentActiveId) {
                            return;
                        }

                        const lastReadIndex = conv.lastReadMessageIndex;
                        const lastMsgIndex = conv.lastMessage?.index;

                        if (lastMsgIndex === undefined || lastMsgIndex === null) {
                            return;
                        }
                        if (lastReadIndex !== null && lastReadIndex >= lastMsgIndex) {
                            return;
                        }

                        const startIndex = lastReadIndex !== null ? lastReadIndex + 1 : 0;
                        const pageSize = Math.min(lastMsgIndex - startIndex + 1, 100);

                        if (pageSize <= 0) return;

                        const msgPage = await conv.getMessages(pageSize, startIndex, "forward");
                        const unreadFromOthers = msgPage.items.filter(
                            (msg) => msg.author !== userChatEmail
                        ).length;

                        if (unreadFromOthers > 0) {
                            conversationMap.set(conv.uniqueName, unreadFromOthers);
                        }
                    } catch (err) {
                        console.log(
                            `Error getting unread count for ${conv.uniqueName}:`,
                            err
                        );
                    }
                })
            );

            // Calculate total unread count based on project conversations
            for (const project of projectsData) {
                if (project.groupChats && Array.isArray(project.groupChats)) {
                    for (const groupChat of project.groupChats) {
                        if (
                            groupChat.groupChatId &&
                            conversationMap.has(groupChat.groupChatId)
                        ) {
                            totalUnreadCount += conversationMap.get(
                                groupChat.groupChatId
                            );
                        }
                    }
                }
            }

            setGlobalUnreadCount(totalUnreadCount);
        } catch (error) {
            console.error("Error calculating global unread count:", error);
        }
    };

    const subtractGlobalUnreadCount = (amountToSubtract) => {
        setGlobalUnreadCount((prev) => Math.max(0, prev - amountToSubtract));
    }

    const updateGlobalUnreadCount = (newCount) => {
        setGlobalUnreadCount(newCount);
    };

    const refreshUnreadCounts = () => {
        if (twilioClient && projects.length > 0) {
            scheduleRecalculation(twilioClient, projects, 300);
        }
    };

    const markConversationAsRead = async (conversationId) => {
        if (!twilioClient || !conversationId) return;

        try {
            const conversation =
                await twilioClient.getConversationByUniqueName(conversationId);
            if (conversation) {
                await conversation.setAllMessagesRead();
                calculateGlobalUnreadCount(twilioClient, projects);
            }
        } catch (error) {
            console.error(
                "Error marking conversation as read:",
                conversationId,
                error
            );
        }
    };

    const refreshProjects = async () => {
        try {
            const projectsResponse = await getAllProjects();
            if (projectsResponse?.data) {
                setProjects(projectsResponse.data);

                if (twilioClient) {
                    calculateGlobalUnreadCount(
                        twilioClient,
                        projectsResponse.data
                    );
                }
            }
        } catch (error) {
            console.error("Error refreshing projects:", error);
        }
    };

    const incrementUnreadCount = () => {
        setGlobalUnreadCount((prev) => prev + 1);
    };

    const decrementUnreadCount = () => {
        setGlobalUnreadCount((prev) => Math.max(0, prev - 1));
    };

    const setActiveConversation = (conversationId) => {
        activeConversationIdRef.current = conversationId;
        setActiveConversationId(conversationId);
    };

    const clearActiveConversation = () => {
        activeConversationIdRef.current = null;
        setActiveConversationId(null);
    };

    const updateProjects = (newProjectData) => {
        if (!newProjectData) {
            console.error("Provide new project data, recieved: ", newProjectData);
            return;
        }
        setProjects(newProjectData);
    };

    const value = {
        globalUnreadCount,
        updateGlobalUnreadCount,
        refreshUnreadCounts,
        refreshProjects,
        incrementUnreadCount,
        decrementUnreadCount,
        twilioClient,
        projects,
        setActiveConversation,
        clearActiveConversation,
        markConversationAsRead,
        updateProjects,
        subtractGlobalUnreadCount
    };

    return (
        <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
    );
};
